/**
 * Improvement Proposals — self-improvement decision queue (S7).
 * Default filter: ready_for_human. Per-row approve/reject actions; the row opens a detail sheet with the audit evidence.
 * Template example: NextAdmin `(dashboard)/(home)/_component/last-transactions-table` and demo `/tables/data-tables`
 * (toolbar with search, filters and a table in one card), under the tabbed page header of `profile/layout.tsx`.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Brain, ClipboardCheck } from 'lucide-react';
import { listImprovementProposalsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import {
  Badge,
  Callout,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  SearchBar,
  Select,
  Toolbar,
} from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/index.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { PAGE_HELP } from '../../help/index.js';
import type { ProposalKind, ProposalRiskClass, ProposalStatus } from '../../services/api/improvements.api.js';
import type { ImprovementProposalSummary } from '../../generated/api/types.gen.js';
import { ImprovementDetail } from './ImprovementDetail.js';
import { ImprovementMetrics } from './ImprovementMetrics.js';
import { ProposalDecisionActions } from './ProposalDecisionActions.js';

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: Array<{ value: ProposalStatus | ''; label: string }> = [
  { value: 'ready_for_human', label: 'Ready for review' },
  { value: '', label: 'All statuses' },
  { value: 'idea', label: 'Idea' },
  { value: 'specced', label: 'Specced' },
  { value: 'implementing', label: 'Implementing' },
  { value: 'auditing', label: 'Auditing' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'applying', label: 'Applying' },
  { value: 'applied', label: 'Applied' },
  { value: 'measured', label: 'Measured' },
  { value: 'failed', label: 'Failed' },
  { value: 'superseded', label: 'Superseded' },
];

const KIND_OPTIONS: Array<{ value: ProposalKind | ''; label: string }> = [
  { value: '', label: 'All kinds' },
  { value: 'finance_bank_entity', label: 'Finance bank entity' },
  { value: 'template_tuning', label: 'Template tuning' },
  { value: 'platform_code', label: 'Platform code' },
  { value: 'data_source', label: 'Data source' },
  { value: 'process', label: 'Process' },
];

const RISK_OPTIONS: Array<{ value: ProposalRiskClass | ''; label: string }> = [
  { value: '', label: 'All risk classes' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const PAGE_TABS = [
  { id: 'queue', label: 'Decision queue' },
  { id: 'design', label: 'Design review' },
  { id: 'metrics', label: 'Loop health' },
] as const;
type PageTabId = (typeof PAGE_TABS)[number]['id'];
const PAGE_TAB_IDS = PAGE_TABS.map((tab) => tab.id);

// ── Badge helpers ──────────────────────────────────────────────────────────────

function statusVariant(status: ProposalStatus) {
  switch (status) {
    case 'ready_for_human':
      return 'warning' as const;
    case 'approved':
    case 'applying':
    case 'applied':
    case 'measured':
      return 'success' as const;
    case 'rejected':
    case 'failed':
      return 'danger' as const;
    default:
      return 'neutral' as const;
  }
}

function riskVariant(risk: ProposalRiskClass) {
  switch (risk) {
    case 'critical':
      return 'danger' as const;
    case 'high':
      return 'warning' as const;
    case 'medium':
      return 'accent' as const;
    default:
      return 'neutral' as const;
  }
}

function formatAge(createdAt: string): string {
  const ms = Date.now() - new Date(createdAt).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) {
    const hours = Math.floor(ms / 3_600_000);
    return hours <= 1 ? 'just now' : `${hours}h ago`;
  }
  return `${days}d ago`;
}

const sentence = (value: string) => {
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// ── Columns ────────────────────────────────────────────────────────────────────

const proposalColumn: DataTableColumn<ImprovementProposalSummary> = {
  key: 'proposal',
  header: 'Proposal',
  sortable: true,
  sortValue: (p) => p.title,
  render: (p) => (
    <div className="max-w-xs">
      <p className="truncate font-medium text-heading">{p.title}</p>
      {p.summary && <p className="mt-0.5 truncate text-xs text-muted">{p.summary}</p>}
    </div>
  ),
};

const kindColumn: DataTableColumn<ImprovementProposalSummary> = {
  key: 'kind',
  header: 'Kind',
  render: (p) => <span className="text-xs text-muted">{sentence(p.kind)}</span>,
};

const riskColumn: DataTableColumn<ImprovementProposalSummary> = {
  key: 'risk',
  header: 'Risk',
  render: (p) => <Badge variant={riskVariant(p.riskClass)}>{sentence(p.riskClass)}</Badge>,
};

const ageColumn: DataTableColumn<ImprovementProposalSummary> = {
  key: 'age',
  header: 'Age',
  align: 'right',
  render: (p) => <span className="whitespace-nowrap text-xs text-muted">{formatAge(p.createdAt)}</span>,
};

const QUEUE_COLUMNS: DataTableColumn<ImprovementProposalSummary>[] = [
  proposalColumn,
  kindColumn,
  riskColumn,
  { key: 'confidence', header: 'Confidence', render: (p) => <span className="text-xs">{p.confidence ?? '—'}</span> },
  {
    key: 'impact',
    header: 'Impact',
    render: (p) => <span className="block max-w-48 truncate text-xs">{p.expectedImpact ?? '—'}</span>,
  },
  {
    key: 'cost',
    header: 'Cost',
    render: (p) => <span className="text-xs">{p.costUsd != null ? `$${p.costUsd}` : '—'}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (p) => <Badge variant={statusVariant(p.status)}>{sentence(p.status)}</Badge>,
  },
  ageColumn,
  {
    key: 'actions',
    header: 'Actions',
    align: 'right',
    render: (p) => (p.status === 'ready_for_human' ? <ProposalDecisionActions proposalId={p.id} gate="final" /> : null),
  },
];

const DESIGN_COLUMNS: DataTableColumn<ImprovementProposalSummary>[] = [
  proposalColumn,
  kindColumn,
  riskColumn,
  { key: 'gate', header: 'Gate', render: () => <Badge variant="warning">Design review</Badge> },
  ageColumn,
  {
    key: 'actions',
    header: 'Actions',
    align: 'right',
    render: (p) => <ProposalDecisionActions proposalId={p.id} gate="design" />,
  },
];

// ── Design-review queue ────────────────────────────────────────────────────────

function DesignReviewQueue({ onSelect }: { onSelect: (id: number) => void }) {
  const help = PAGE_HELP['improvements'];
  const query = useQuery(
    listImprovementProposalsOptions({ query: { pendingDesignReview: 'true', limit: 100, offset: 0 } }),
  );
  const rows = query.data?.data ?? [];

  return (
    <div className="space-y-5">
      <Callout tone="warning" title="Early design gate">
        These high-risk proposals are paused at the <strong>design review</strong> stage, before any implementation
        begins. Approving the design lets the AI proceed to implementation and auditing; a{' '}
        <strong>separate final decision</strong> is required once implementation is complete. Rejecting here stops the
        proposal permanently.
      </Callout>
      <Card>
        {query.isError && !query.isLoading ? (
          <Callout tone="danger">Failed to load design reviews.</Callout>
        ) : (
          <DataTable
            aria-label="Proposals pending design review"
            columns={DESIGN_COLUMNS}
            rows={rows}
            rowKey={(p) => p.id}
            onRowClick={(p) => onSelect(p.id)}
            loading={query.isLoading}
            pageSize={20}
            footer={query.data?.paging ? `${query.data.paging.total} total · showing ${rows.length}` : undefined}
            empty={
              <EmptyState
                icon={ClipboardCheck}
                title="No proposals pending design review"
                description="High-risk proposals that need design approval before implementation begins will appear here."
                learnItems={help?.emptyState.learnItems}
              />
            }
          />
        )}
      </Card>
    </div>
  );
}

// ── Decision queue ─────────────────────────────────────────────────────────────

function DecisionQueue({ onSelect }: { onSelect: (id: number) => void }) {
  const help = PAGE_HELP['improvements'];
  const [status, setStatus] = useState<ProposalStatus | ''>('ready_for_human');
  const [kind, setKind] = useState<ProposalKind | ''>('');
  const [riskClass, setRiskClass] = useState<ProposalRiskClass | ''>('');
  const [search, setSearch] = useState('');

  const query = useQuery(
    listImprovementProposalsOptions({
      query: {
        status: status || undefined,
        kind: kind || undefined,
        riskClass: riskClass || undefined,
        limit: 100,
        offset: 0,
      },
    }),
  );

  const q = search.trim().toLowerCase();
  const rows = (query.data?.data ?? []).filter(
    (p) => !q || [p.title, p.summary, p.kind, p.sourceArm].some((v) => v?.toLowerCase().includes(q)),
  );

  return (
    <Card>
      <Toolbar
        search={<SearchBar value={search} onChange={setSearch} placeholder="Search title or summary…" />}
        right={
          <>
            <Select
              aria-label="Status"
              className="w-auto!"
              value={status}
              onChange={(e) => setStatus(STATUS_OPTIONS.find((o) => o.value === e.target.value)?.value ?? '')}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Kind"
              className="w-auto!"
              value={kind}
              onChange={(e) => setKind(KIND_OPTIONS.find((o) => o.value === e.target.value)?.value ?? '')}
            >
              {KIND_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Risk class"
              className="w-auto!"
              value={riskClass}
              onChange={(e) => setRiskClass(RISK_OPTIONS.find((o) => o.value === e.target.value)?.value ?? '')}
            >
              {RISK_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </>
        }
      />
      {query.isError && !query.isLoading ? (
        <Callout tone="danger">Failed to load improvement proposals.</Callout>
      ) : (
        <DataTable
          aria-label="Improvement proposals"
          columns={QUEUE_COLUMNS}
          rows={rows}
          rowKey={(p) => p.id}
          onRowClick={(p) => onSelect(p.id)}
          loading={query.isLoading}
          pageSize={20}
          footer={query.data?.paging ? `${query.data.paging.total} total · showing ${rows.length}` : undefined}
          empty={
            <EmptyState
              icon={Brain}
              title={help?.emptyState.title ?? 'No proposals'}
              description={help?.emptyState.description ?? 'No improvement proposals match your filters.'}
              learnItems={help?.emptyState.learnItems}
            />
          }
        />
      )}
    </Card>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function ImprovementsPage() {
  const [activeTab, setActiveTab] = useTabParam<PageTabId>(PAGE_TAB_IDS, 'queue');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  return (
    <DetailPage
      title="Improvement proposals"
      subtitle="AI-authored platform improvements awaiting review and approval"
      helpContent={PAGE_HELP['improvements']}
      tabs={PAGE_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {activeTab === 'metrics' && <ImprovementMetrics />}
      {activeTab === 'design' && <DesignReviewQueue onSelect={setSelectedId} />}
      {activeTab === 'queue' && <DecisionQueue onSelect={setSelectedId} />}
      {selectedId != null && <ImprovementDetail proposalId={selectedId} onClose={() => setSelectedId(null)} />}
    </DetailPage>
  );
}

export default ImprovementsPage;
